/**
 * P2-S5 Deep Linking 마무리 활동 만들기(SR-INT-01 · 계약 §2.1 — 교사 세션 · deep link 설정은 제어 평면 세션에):
 *   LMS 의 LtiDeepLinkingRequest 실행 → 제어 평면이 <셸>/teacher/deeplink#ticket=<t> 로 보낸다(/launch 와 같은 티켓 교환 — 세션이
 *   서야 라우트 가드가 화면을 연다 · 실패는 /launch#error=FE-304).
 *   GET  /api/courses/{c}/teacher/deeplink → { deepLink, recommendedModeId, modes:[{id, kind, recommended?}],
 *        presets:{ <modeId>: [presetKey…] }, templates, defaults:{ timeBudgetS, visibility, autoComplete } }
 *   POST /api/courses/{c}/teacher/deeplink { title(1..60), modeId, presetKey?, timeBudgetS(300..1200), visibility, autoComplete,
 *        scriptTemplate, scheduledAt?(ISO) } → 200 { returnUrl, jwt, activityId }(LtiDeepLinkingResponse — 셸이 폼 POST 로 돌려준다)
 *        · 422 { field } · 409 { field: "deepLink" }(Deep Linking 실행이 아니거나 이미 한 번 돌려보냈다 — 실행마다 한 번)
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import type { Visibility } from "../../../lib/api/fieldApi";
import { readTemplates } from "../../room";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import { InvalidField } from "../../teacher-settings";
import type { Requester } from "../../../types/api";
import type { DeepLinkOptions, DeepLinkBody } from "../types";

export interface DeepLinkApi {
  options(): Promise<DeepLinkOptions>;
  create(
    body: DeepLinkBody,
  ): Promise<{ returnUrl: string; jwt: string; activityId: string | null }>;
}

/** 409 — Deep Linking 실행이 아니거나 이미 돌려보냈다 */
export class DeepLinkUsed extends Error {
  constructor() {
    super("deep link used");
    this.name = "DeepLinkUsed";
  }
}

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const VIS: readonly Visibility[] = ["class", "selected", "private"];

export function readOptions(v: unknown): DeepLinkOptions {
  const o = obj(v);
  const d = obj(o.defaults);
  const modes = (Array.isArray(o.modes) ? o.modes : [])
    .map(obj)
    .filter((m) => typeof m.id === "string")
    .map((m) => ({
      id: String(m.id),
      kind: typeof m.kind === "string" ? m.kind : "",
      recommended: m.recommended === true,
    }));
  // 프리셋 = { 모드: [키…] }
  const presets: Record<string, string[]> = {};
  for (const [m, keys] of Object.entries(obj(o.presets)))
    if (Array.isArray(keys))
      presets[m] = [...new Set(keys.filter((k): k is string => typeof k === "string"))];
  const t = Number(d.timeBudgetS);
  return {
    deepLink: o.deepLink !== false,
    recommendedModeId:
      typeof o.recommendedModeId === "string"
        ? o.recommendedModeId
        : (modes.find((m) => m.recommended)?.id ?? null),
    modes,
    presets,
    templates: readTemplates(o.templates),
    defaults: {
      timeBudgetS: Number.isFinite(t) && t >= 300 && t <= 1200 ? t : 600,
      visibility: VIS.includes(d.visibility as Visibility) ? (d.visibility as Visibility) : "class",
      autoComplete: d.autoComplete === true,
    },
  };
}

export function createDeepLinkApi(request: Requester, courseId: string): DeepLinkApi {
  const path = `/api/courses/${encodeURIComponent(courseId)}/teacher/deeplink`;
  return {
    options: async () => readOptions(await request<unknown>(path)),
    async create(body) {
      try {
        const r = obj(
          await request<unknown>(path, {
            method: "POST",
            body: JSON.stringify(body),
            headers: { "Content-Type": "application/json" },
          }),
        );
        if (typeof r.returnUrl !== "string" || typeof r.jwt !== "string")
          throw new Error("deeplink response");
        return {
          returnUrl: r.returnUrl,
          jwt: r.jwt,
          activityId: typeof r.activityId === "string" ? r.activityId : null,
        };
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) throw new DeepLinkUsed();
        if (e instanceof ApiError && e.status === 422) {
          const b = obj(e.body);
          throw new InvalidField(typeof b.field === "string" ? b.field : "");
        }
        throw e;
      }
    },
  };
}

export function useDeepLinkApi(): DeepLinkApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createDeepLinkApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
